import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Paper,
  Button,
  Skeleton,
} from '@mui/material';
import { Star as StarIcon, AutoAwesome as AutoAwesomeIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { AdminService } from '../../services/adminService';

function RatingStars({ rating }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <StarIcon
          key={i}
          fontSize="small"
          sx={{ color: i <= rating ? '#f59e0b' : '#d1d5db' }}
        />
      ))}
    </Box>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

function SynthesisCard({ total }) {
  const [synthesis, setSynthesis] = useState(null);
  const [avgRating, setAvgRating] = useState(null);
  const [synthCount, setSynthCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generated, setGenerated] = useState(false);

  const generate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await AdminService.getParentFeedbackSynthesis();
      if (data.synthesis) {
        setSynthesis(data.synthesis);
        setAvgRating(data.avgRating);
        setSynthCount(data.count);
        setGenerated(true);
      } else {
        setSynthesis(null);
        setGenerated(true);
      }
    } catch {
      setError('Impossible de générer la synthèse.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-génère dès qu'il y a des feedbacks
  useEffect(() => {
    if (total > 0 && !generated && !loading) {
      generate();
    }
  }, [total, generated, loading, generate]);

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2.5, mb: 3, borderColor: 'primary.light', bgcolor: 'primary.50' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <AutoAwesomeIcon fontSize="small" color="primary" />
        <Typography variant="subtitle1" fontWeight={600}>
          Synthèse IA
        </Typography>
        {synthCount != null && avgRating != null && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            {synthCount} feedback{synthCount > 1 ? 's' : ''} analysés · note moyenne {avgRating}/5
          </Typography>
        )}
        {generated && !loading && (
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={generate}
            sx={{ ml: 'auto' }}
          >
            Regénérer
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

      {loading && (
        <Box>
          <Skeleton variant="text" width="90%" />
          <Skeleton variant="text" width="75%" />
          <Skeleton variant="text" width="85%" />
          <Skeleton variant="text" width="60%" />
        </Box>
      )}

      {!loading && synthesis && (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
          {synthesis}
        </Typography>
      )}

      {!loading && generated && !synthesis && !error && (
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          Aucun feedback disponible pour générer une synthèse.
        </Typography>
      )}
    </Paper>
  );
}

export default function ParentFeedbackScreen() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [ratingFilter, setRatingFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFeedbacks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: page + 1, limit: rowsPerPage };
      if (ratingFilter) params.rating = ratingFilter;
      const data = await AdminService.getParentFeedbacks(params);
      setFeedbacks(data.feedbacks || []);
      setTotal(data.total || 0);
    } catch {
      setError('Impossible de charger les feedbacks.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, ratingFilter]);

  useEffect(() => {
    fetchFeedbacks();
  }, [fetchFeedbacks]);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Feedbacks parents
      </Typography>

      <SynthesisCard total={total} />

      {/* Filtres */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Note</InputLabel>
          <Select
            value={ratingFilter}
            label="Note"
            onChange={e => { setRatingFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="">Toutes</MenuItem>
            {[5, 4, 3, 2, 1].map(r => (
              <MenuItem key={r} value={r}>{r} étoile{r > 1 ? 's' : ''}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="body2" color="text.secondary">
          {total} feedback{total !== 1 ? 's' : ''} reçu{total !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Parent</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell>Commentaire</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {feedbacks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      Aucun feedback pour cette sélection.
                    </TableCell>
                  </TableRow>
                ) : (
                  feedbacks.map(f => (
                    <TableRow key={f.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDate(f.submittedAt)}
                      </TableCell>
                      <TableCell>{f.displayName || '—'}</TableCell>
                      <TableCell>{f.email || '—'}</TableCell>
                      <TableCell>
                        <Tooltip title={`${f.rating}/5`}>
                          <Box>
                            <RatingStars rating={f.rating} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 480 }}>
                        {f.comment ? (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {f.comment}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            (sans commentaire)
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Lignes :"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
          />
        </>
      )}
    </Box>
  );
}
